import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { style } from './slide-in.style.js'
import { hasNamedSlot } from 'elements/_shared/hasSlot.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'
import { animateTo, ElementAnimation } from 'elements/_shared/animations.js'
import { scrollLock, scrollUnlock } from 'elements/_shared/scroll-lock.js'


type Placement = 'left' | 'right'

export const slideInSizes = ['default', 'large'] as const
export type slideInSizesType = typeof slideInSizes

@customElement('cse-slide-in')
export class CSESlideIn extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style, fontStyles]

  @property({ type: String })
  size: slideInSizesType[number] = 'default'

  @property({ type: Boolean, attribute: 'no-backdrop' })
  noBackdrop = false

  @property({ type: String })
  placement: Placement = 'right'

  @property({ type: Boolean, reflect: true, attribute: 'open' })
  isOpen = false

  @query('dialog')
  dialog!: HTMLDialogElement

  private isAnimating = false

  private animations = {
    left: {
      open: {
        keyframes: [
          { opacity: 0, translate: '-100%' },
          { opacity: 1, translate: '0' }
        ],
        options: { duration: 300, easing: 'ease-in-out' }
      },
      close: {
        keyframes: [
          { opacity: 1, translate: '0' },
          { opacity: 0, translate: '-100%' }
        ],
        options: { duration: 300, easing: 'ease-in-out' }
      }
    },
    right: {
      open: {
        keyframes: [
          { opacity: 0, translate: '100%' },
          { opacity: 1, translate: '0' }
        ],
        options: { duration: 300, easing: 'ease-in-out' }
      },
      close: {
        keyframes: [
          { opacity: 1, translate: '0' },
          { opacity: 0, translate: '100%' }
        ],
        options: { duration: 300, easing: 'ease-in-out' }
      }
    }
  }

  constructor() {
    super()
    this.addEventListener('click', this.clickHandler)
  }

  clickHandler = (e: MouseEvent) => {
    if (
      this.isOpen &&
      !this.isEventInElement(e, this.dialog)
    ) {
      this.close()
    }
  }

  isEventInElement = (event: MouseEvent, element: Element) => {
    const path = event.composedPath()
    return !(path[0] == element)
  }

  override firstUpdated() {
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault()
      this.close()
    })
  }

  override async getUpdateComplete() {
    const result = await super.getUpdateComplete()
    await this.animationComplete
    return result
  }

  animationComplete: Promise<void> = Promise.resolve()

  open = async () => {
    if (this.isOpen || this.isAnimating) { return }

    let resolveAnimation!: () => void
    this.animationComplete = new Promise((resolve) => { resolveAnimation = resolve })

    this.dialog.showModal()
    this.isAnimating = true

    const animation: ElementAnimation = this.animations[this.placement].open
    await animateTo(this.dialog, animation.keyframes, animation.options).then(() => {
      this.isAnimating = false
      this.isOpen = true
      resolveAnimation()
      scrollLock(this.dialog)
    })
  }

  close = async () => {
    if (!this.isOpen || this.isAnimating) { return }

    let resolveAnimation!: () => void
    this.animationComplete = new Promise((resolve) => { resolveAnimation = resolve })

    scrollUnlock(this.dialog)
    this.isAnimating = true
    
    const animation: ElementAnimation = this.animations[this.placement].close
    await animateTo(this.dialog, animation.keyframes, animation.options).then(() => {
      this.isAnimating = false
      this.isOpen = false
      this.dialog.close()
      resolveAnimation()
    })
  }

  private renderHeader = (hasHeader: boolean) => html`
    <div class="${classMap({
      header: true,
      'font-heading-4': true,
      'header--no-content': !hasHeader
    })}">
      ${hasHeader ? html`<slot name="header"></slot>` : nothing}
      <cse-icon-button
        @click="${this.close}"
        aria-label="${t('close_slide_in', { ns: 'cse-slide-in', default: 'Close slide-in' })}"
      ></cse-icon-button>
    </div>
  `

  private renderBody = () => html`
    <div class="body">
      <slot name=""></slot>
    </div>
  `

  private renderFooter = () => html`
    <cse-sticky-bar class="footer" position="bottom">
      <slot name="footer"></slot>
    </cse-sticky-bar>
  `

  override render() {
    const hasHeader = hasNamedSlot(this, 'header')
    const hasFooter = hasNamedSlot(this, 'footer')

    return html`
      <dialog
        class="${classMap({
          'cse-slide-in': true,
          [`placement-${this.placement}`]: true,
          'no-backdrop': this.noBackdrop,
          [`size-${this.size}`]: true,
        })}"
      >
        <div class="content">
          ${this.renderHeader(hasHeader)}
          ${this.renderBody()}
          ${hasFooter ? this.renderFooter() : nothing}
        </div>
      </dialog>
    `
  }
}


declare global {
  interface HTMLElementTagNameMap {
    'cse-slide-in': CSESlideIn
  }
}
